/**
 * Validates and sanitizes URL params before deserialization.
 * Invalid values fall back gracefully — no crashes, no undefined behavior.
 */

export function validateNumeric(value: string, allowZero = true): number | null {
  const num = Number(value);
  if (isNaN(num)) return null;
  if (num < 0) return null;
  if (!allowZero && num === 0) return null;
  return num;
}

export function validateEnum<T extends string>(value: string, validValues: Set<string>): T | null {
  return validValues.has(value) ? (value as T) : null;
}

export function validateEnumList<T extends string>(values: Array<string>, validValues: Set<string>): Array<T> {
  return values.filter((v) => validValues.has(v)) as Array<T>;
}

export function validateDateString(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(value + 'T00:00:00');
  if (isNaN(date.getTime())) return null;
  return value;
}

export function normalizeMoveInDate(
  start: string | null,
  end: string | null,
): { start: string | null; end: string | null } {
  return { start: null, end: end || start || null };
}

const LAT_MAX = 90;
const LON_MAX = 180;

/**
 * Parses a lat/lon pair. Unlike validateNumeric this accepts negatives
 * (US longitudes are negative) but bounds to valid geographic ranges.
 */
export function validateCoordinate(
  latStr: string | undefined,
  lonStr: string | undefined,
): { lat: number; lon: number } | null {
  if (!latStr || !lonStr) return null;
  const lat = Number(latStr);
  const lon = Number(lonStr);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > LAT_MAX || Math.abs(lon) > LON_MAX) return null;
  return { lat, lon };
}

export function validateRange(
  min: number | null,
  max: number | null,
): { min: number | null; max: number | null } {
  if (min !== null && max !== null && min > max) {
    return { min: max, max: min };
  }
  return { min, max };
}
